import { createContext, useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Notification } from '@components/notification';
import { Toast, ToastContextType, ToastProviderProps } from '@contexts/toast/toast.type';
import uuid4 from 'uuid4';

export const DEFAULT_INTERVAL = 5000;

export const ToastContext = createContext<ToastContextType>({
  data: [],
  push: () => {},
  remove: () => {},
});

const ToastContainer = () => {
  const toastContext = useContext(ToastContext);

  function handleRemove(id: string) {
    toastContext.remove(id);
  }

  return (
    <div
      aria-live='assertive'
      className='fixed z-50 inset-0 flex flex-col justify-start items-center space-y-8 px-4 py-6 pointer-events-none sm:items-end sm:p-6'>
      {toastContext.data.map((toast) => {
        return (
          <Notification
            type={toast.type}
            key={toast.id}
            id={toast.id}
            title={toast.title}
            text={toast.text}
            onRemove={handleRemove}
            lifetime={toast.lifetime}
            disableTimeOut={toast.disableTimeOut}
          />
        );
      })}
    </div>
  );
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [data, setData] = useState<Array<Toast>>([]);

  const Push = useCallback(
    ({ title, text, lifetime, type, disableTimeOut = false }: Toast) => {
      const newItem: Toast = {
        id: uuid4(),
        title,
        text,
        type,
        lifetime: lifetime ? lifetime : DEFAULT_INTERVAL,
        disableTimeOut,
      };

      setData((prevState) => [...prevState.filter((toast) => toast.title !== title), newItem]);
    },
    [setData]
  );

  const toastContext = useCallback(() => {
    return {
      data,
      push: Push,
      async remove(id: string) {
        setData((prevState) => prevState.filter((e) => e.id != id));
      },
    };
  }, [data, setData, Push]);

  return (
    <ToastContext.Provider value={toastContext()}>
      {createPortal(<ToastContainer />, document.body as Element)}
      {children ? children : null}
    </ToastContext.Provider>
  );
};
